import React, { useState, useEffect } from 'react';
import { MapPin, Clock, CheckCircle, Zap, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const InterventionMap = () => {
  const [activeInterventions, setActiveInterventions] = useState([]);
  const [recentCount, setRecentCount] = useState(0);

  // Simulate real-time interventions
  useEffect(() => {
    const girondeCities = [
      'Bordeaux', 'Mérignac', 'Pessac', 'Talence', 'Villenave-d\'Ornon',
      'Bègles', 'Cenon', 'Floirac', 'Gradignan', 'Le Bouscat', 'Lormont',
      'Saint-Médard-en-Jalles', 'Bruges', 'Eysines', 'Blanquefort',
      'Libourne', 'Arcachon', 'La Teste-de-Buch', 'Gujan-Mestras'
    ];

    const interventionTypes = [
      { type: 'Frelon asiatique', icon: '🐝', color: 'text-red-600', bg: 'bg-red-100' },
      { type: 'Nid de guêpes', icon: '🐞', color: 'text-orange-600', bg: 'bg-orange-100' },
      { type: 'Frelon européen', icon: '🐛', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    ];

    const timeLabels = ['À l\'instant', 'Il y a 15min', 'Il y a 1h', 'Il y a 2h', 'Aujourd\'hui', 'Hier'];

    const generateIntervention = () => {
      const city = girondeCities[Math.floor(Math.random() * girondeCities.length)];
      const intervention = interventionTypes[Math.floor(Math.random() * interventionTypes.length)];
      const time = timeLabels[Math.floor(Math.random() * timeLabels.length)];
      
      return {
        id: Date.now() + Math.random(),
        city,
        ...intervention,
        time,
        x: 10 + Math.random() * 80,
        y: 20 + Math.random() * 60
      };
    };

    // Initial interventions
    const initial = Array.from({ length: 5 }, generateIntervention);
    setActiveInterventions(initial);
    setRecentCount(12 + Math.floor(Math.random() * 8));

    // Add new interventions periodically
    const interval = setInterval(() => {
      setActiveInterventions(prev => {
        const newIntervention = generateIntervention();
        const updated = [newIntervention, ...prev.slice(0, 7)];
        return updated;
      });
      setRecentCount(prev => prev + 1);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-700">{recentCount}</div>
            <div className="text-sm text-green-600">Interventions 24h</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-700">847</div>
            <div className="text-sm text-blue-600">Clients satisfaits</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-yellow-700">&lt; 2h</div>
            <div className="text-sm text-yellow-600">Temps moyen</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4 text-center">
            <Zap className="h-6 w-6 text-red-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-red-700">7j/7</div>
            <div className="text-sm text-red-600">Disponibilité</div>
          </CardContent>
        </Card>
      </div>

      {/* Interactive Map */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="relative h-96 bg-gradient-to-br from-blue-50 via-green-50 to-yellow-50">
            {/* Map Background */}
            <div className="absolute inset-0 opacity-20">
              <svg viewBox="0 0 400 300" className="w-full h-full">
                {/* Gironde outline approximation */}
                <path 
                  d="M50,50 Q100,30 150,50 L200,40 Q250,45 300,60 L350,80 Q360,120 350,160 L340,200 Q320,240 280,260 L200,270 Q150,275 100,260 L60,240 Q40,200 45,160 L50,120 Q45,80 50,50 Z" 
                  fill="rgba(34, 197, 94, 0.1)" 
                  stroke="rgba(34, 197, 94, 0.3)" 
                  strokeWidth="2"
                />
                {/* Bordeaux marker */}
                <circle cx="180" cy="150" r="4" fill="rgb(59, 130, 246)" />
                <text x="190" y="155" fontSize="12" fill="rgb(59, 130, 246)" fontWeight="bold">Bordeaux</text>
              </svg>
            </div>

            {/* Intervention Markers */}
            {activeInterventions.map((intervention, index) => (
              <div
                key={intervention.id}
                className={`absolute transform transition-all duration-1000 ${
                  index === 0 ? 'animate-bounce' : ''
                }`}
                style={{ 
                  left: `${intervention.x}%`, 
                  top: `${intervention.y}%`,
                  animationDelay: `${index * 0.2}s`
                }}
              >
                <div className={`relative group cursor-pointer`}>
                  {/* Pulse Animation for recent interventions */}
                  {index < 2 && (
                    <div className="absolute inset-0 bg-red-400 rounded-full animate-ping opacity-75"></div>
                  )}
                  
                  {/* Marker */}
                  <div className={`relative w-8 h-8 rounded-full ${intervention.bg} border-2 border-white shadow-lg flex items-center justify-center transform group-hover:scale-125 transition-transform duration-200`}>
                    <span className="text-sm">{intervention.icon}</span>
                  </div>

                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-black text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    <div className="font-semibold">{intervention.type}</div>
                    <div>{intervention.city}</div>
                    <div className="text-gray-300">{intervention.time}</div>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                  </div>
                </div>
              </div>
            ))}

            {/* Legend */}
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
              <h4 className="font-semibold text-sm mb-2">🎯 Interventions en cours</h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-100 border border-red-300 rounded-full"></div>
                  <span>Frelon asiatique</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-orange-100 border border-orange-300 rounded-full"></div>
                  <span>Nid de guêpes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded-full"></div>
                  <span>Frelon européen</span>
                </div>
              </div>
            </div>

            {/* Live Activity Feed */}
            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg max-w-xs">
              <h4 className="font-semibold text-sm mb-2 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Activité en direct
              </h4>
              <div className="space-y-2 text-xs max-h-20 overflow-hidden">
                {activeInterventions.slice(0, 3).map((intervention, index) => (
                  <div key={intervention.id} className={`text-gray-700 ${index === 0 ? 'font-semibold' : ''}`}>
                    <span className={intervention.color}>{intervention.icon}</span> {intervention.city} - {intervention.time}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InterventionMap;